import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatRelativeTime } from "../../utils";
import { highlightHtmlContent } from "../../utils";
import ExpandedStackView from './ExpandedStackView';
import CollapsedStackView from './CollapsedStackView';

const ProjectStack = ({
  project,
  stackedJobs,
  expandedProjects,
  toggleProjectExpansion,
  expandedStacks,
  toggleStackExpansion,
  searchTerm,
  hasActiveFilters,
  openAiDialogForJob,
  TASK_STATUSES,
  ALL_TASKS_TAB,
  renderStackedImageIcon,
  handleRowClick,
  handleRowDoubleClick,
  handleAddFromRow,
  selectedRow,
  handleEdit,
  handleDelete,
  handleViewAttachments,
  handleReplyToNote,
  handlePriorityClick,
  handlePriorityMouseEnter,
  handlePriorityMouseLeave,
  handleNoteTextMouseEnter,
  handleNoteTextMouseLeave,
  handleLinkedNoteClick,
  handleLinkedNoteMouseEnter,
  handleLinkedNoteMouseLeave,
  isNoteReply,
  getReplyNoteId,
  isOriginalNoteExists,
  userStatusMap,
  loadingUsers,
  getPriorityValue,
  manuallyUpdatedPriorities,
  shouldShowNotePopup,
  userId,
  tasksByJob,
  loadingTasksByJob,
  expandedTasks,
  toggleTaskExpansion,
  notesByTask,
  loadingNotesByTask,
  fetchTasksForJob,
  fetchNotesForTask,
  onTaskEdit,
  onTaskDelete,
  onTaskStatusChange,
  onTaskClick,
  refreshTasksForJob,
  onFilterByProject,
  onFilterByWorkspace,
  refreshNotes,
  highlightedTaskId,
  clearHighlightedTask,
  loadMoreTaskNotes,
  taskPagination,
  onOpenEdit,
  onCreateTask,
  onOpenSlideshow,
  activeTabByJob,
  onTabChange,
  assigneeFilters,
  loadingUsersByJob,
  onAssigneeFilter,
  jobUsers,
  jobUsersTask,
  loadingUsersByJobTask,
  clearAssigneeFilter,
  fetchJobPermissionUsers,
  workspaceRole,
  onDenyUserClick,
  fetchJobTaskUsers,
}) => {
  const [expandedJob, setExpandedJob] = useState(null);
  const isProjectExpanded = expandedProjects[project.projectId];
  const projectJobs = (stackedJobs || []).filter(job => job && job.projectId === project.projectId);

  const getExpandedTaskForJob = (jobId) => {
    const jobTasks = tasksByJob[jobId] || [];
    const expandedTaskId = Object.keys(expandedTasks || {}).find(
      (taskId) =>
        expandedTasks[taskId] &&
        jobTasks.some((t) => t.id.toString() === taskId)
    );
    if (expandedTaskId) {
      return jobTasks.find((t) => t.id.toString() === expandedTaskId);
    }
    return null;
  };

  const currentExpandedJob = (projectJobs || []).find(job => expandedStacks && job && job.jobName && expandedStacks[job.jobName]);
  const expandedTaskForExpandedJob = currentExpandedJob ? getExpandedTaskForJob(currentExpandedJob.jobId) : null;

  useEffect(() => {
    const job = (projectJobs || []).find(job => expandedStacks && job && job.jobName && expandedStacks[job.jobName]);
    setExpandedJob(job || null);
  }, [expandedStacks, projectJobs]);

  const onOpenProjectEdit = (project) => {
    console.log("Opening edit for project:", project);
  };

  const totalNotes = projectJobs.reduce((sum, job) => sum + (job.noteCount || 0), 0);
  const totalJobs = projectJobs.length;
  const latestJob = projectJobs.sort((a, b) =>
    new Date(b.latestTimeStamp || 0) - new Date(a.latestTimeStamp || 0)
  )[0];

  if (!isProjectExpanded) {
    const maxLayers = Math.min(totalJobs, 5);

    return (
      <div
        className="project-stack collapsed"
        style={{
          cursor: "pointer",
          position: "relative",
          height: "280px",
          width: "300px",
          flexShrink: 0,
          marginRight: "0px",
        }}
        onClick={() => toggleProjectExpansion(project.projectId)}
      >
        {/* Render cards in REVERSE order so the top card (with metadata) is on top */}
        {[...Array(maxLayers)].map((_, idx) => {
          // Calculate the actual index in reverse (0 becomes top card)
          const index = (maxLayers - 1) - idx;
          const isTopCard = idx === maxLayers - 1; // Last rendered card is on top

          return (
            <div
              key={`project-layer-${index}`}
              className="stack-layer-card"
              style={{
                position: "absolute",
                left: `${index * 15}px`,
                top: 0,
                transform: `rotate(${index * -2}deg)`,
                zIndex: maxLayers - index,
                width: "300px",
                height: "250px",
                opacity: 1 - index * 0.15,
                transition: "all 0.3s ease",
                overflow: "hidden",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {isTopCard ? (
                <div style={{ padding: "15px", height: "100%", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", paddingBottom: "10px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
                        <i className="fas fa-folder" style={{ color: "#14A2B6", flexShrink: 0 }} />
                        <div style={{ fontWeight: 600, color: "#2c3e50", fontSize: "16px", minWidth: 0 }}>
                          <span style={{ lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px", display: "block" }} title={project.projectName}>
                            {project.projectName}
                          </span>
                          {project.workspaceName && (
                            <span style={{ fontSize: "12px", color: "#7f8c8d", fontWeight: 400, marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px", display: "block" }}>
                              {project.workspaceName}
                            </span>
                          )}
                        </div>
                      </div>
                      {project.projectDescription && (
                        <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {project.projectDescription}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "#14A2B6", color: "white", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                        <i className="fas fa-layer-group" />
                        <span>{totalNotes} notes</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px", minHeight: 0, overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                      <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#14A2B6", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "bold", flexShrink: 0 }}>
                        {latestJob?.lastSiteNoteUserName?.charAt(0).toUpperCase() || project.projectName.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: "14px", color: "#555", flexShrink: 0 }}>
                        {latestJob?.lastSiteNoteUserName || "No activity"}
                      </span>
                    </div>

                    <div style={{ fontSize: "13px", color: "#666", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", padding: "8px", backgroundColor: "#f8f9fa", borderRadius: "4px", borderLeft: "3px solid #14A2B6", marginTop: "8px", position: "relative", maxHeight: "70px", flexShrink: 0 }}>
                      <div className="note-text-preview" style={{ fontSize: "12px", color: "#666", lineHeight: 1.4, maxHeight: "50px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}
                        dangerouslySetInnerHTML={{ __html: highlightHtmlContent(latestJob?.lastSiteNote || "No recent notes", searchTerm) }}
                      />
                    </div>

                    <div style={{ fontSize: "11px", color: "#888", marginTop: "auto", paddingTop: "8px", borderTop: "1px dashed #e9ecef", display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <i className="fas fa-clock" style={{ fontSize: "10px" }} />
                        <span>{latestJob?.latestTimeStamp ? formatRelativeTime(latestJob.latestTimeStamp) : "No updates"}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <i className="fas fa-briefcase" style={{ fontSize: "12px", color: "#6c757d" }} />
                        <span style={{ fontSize: "11px" }}>{totalJobs} {totalJobs === 1 ? "Job" : "Jobs"}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: "center", paddingTop: "10px", borderTop: "1px dashed #e9ecef", marginTop: "10px", flexShrink: 0 }}>
                    <div style={{ fontSize: "12px", color: "#7f8c8d", fontStyle: "italic" }}>
                      {totalJobs === 1 ? "Click to expand job" : "Click to expand project"}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ height: "100%", background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#bdc3c7" }}>
                  <i className="fas fa-folder" style={{ fontSize: "24px" }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="project-stack expanded" style={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      marginBottom: "0px",
      overflow: "hidden"
    }}>
      <div className={`expanded-stack-header ${expandedJob ? "with-job" : ""} ${expandedTaskForExpandedJob ? "with-task" : ""}`} style={{ flexShrink: 0 }}>
        <div className="expanded-stack-left">
          <div className="expanded-stack-job-info">
            <i className="fas fa-folder" style={{ color: "#14A2B6" }} />
            <div className="expanded-stack-job-details">
              <span className="expanded-stack-job-name">{project.projectName}</span>
              <small className="expanded-stack-project-name" title={project.workspaceName}>
                {project.workspaceName} • {totalJobs} jobs • {totalNotes} notes
              </small>
            </div>
          </div>

          {expandedJob && (
            <>
              <i className="fas fa-chevron-right" />
              <div className="expanded-stack-task-info">
                <i className="fas fa-briefcase" />
                <div className="expanded-stack-task-details">
                  <span className="expanded-stack-task-title">
                    {expandedJob.jobName}
                  </span>
                </div>
              </div>
            </>
          )}

          {expandedTaskForExpandedJob && (
            <>
              <i className="fas fa-chevron-right" />
              <div className="expanded-stack-task-info">
                <i className="fas fa-tasks" />
                <div className="expanded-stack-task-details">
                  {expandedTaskForExpandedJob.friendlyId && (
                    <span className="expanded-stack-task-id">
                      {expandedTaskForExpandedJob.friendlyId}
                    </span>
                  )}
                  <span className="expanded-stack-task-title">
                    {expandedTaskForExpandedJob.title}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="expanded-stack-actions">
          {expandedJob && !expandedTaskForExpandedJob && (
            <div className="expanded-stack-badge task-badge">
              <i className="fas fa-tasks" />
              <span>
                {(tasksByJob[expandedJob.jobId] || []).length} {(tasksByJob[expandedJob.jobId] || []).length === 1 ? "Task" : "Tasks"}
              </span>
            </div>
          )}

          <button
            className="expanded-stack-action-btn slideshow"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSlideshow(expandedJob || projectJobs[0], 0);
            }}
            title="View slideshow"
          >
            <i className="fas fa-images" />
          </button>

          {expandedJob && !expandedTaskForExpandedJob && (
            <button
              className="expanded-stack-action-btn create-task"
              onClick={(e) => {
                e.stopPropagation();
                onCreateTask(expandedJob);
              }}
              title="Create Task"
            >
              <i className="fas fa-plus-circle" />
            </button>
          )}

          <button
            className="expanded-stack-action-btn ai-summarize"
            onClick={(e) => {
              e.stopPropagation();
              openAiDialogForJob(expandedJob || projectJobs[0]);
            }}
            title="Summarize notes (AI)"
          >
            <i className="fas fa-comments" />
          </button>

          <button
            className="expanded-stack-action-btn settings"
            onClick={(e) => {
              e.stopPropagation();
              onOpenProjectEdit(project);
            }}
            title="Edit Project"
          >
            <i className="fas fa-cog" />
          </button>

          <button
            className="expanded-stack-back-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (expandedTaskForExpandedJob) {
                toggleTaskExpansion(expandedTaskForExpandedJob.id, expandedJob.jobId);
              } else if (expandedJob) {
                toggleStackExpansion(expandedJob.jobName, expandedJob.jobId);
                setExpandedJob(null);
              } else {
                toggleProjectExpansion(project.projectId);
              }
            }}
            title="Back"
          >
            <i className="fas fa-arrow-left" style={{ color: "white" }} />
          </button>
        </div>
      </div>

      <div style={{
        padding: "-13px",
        backgroundColor: "#fafafa",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        overflow: "visible"
      }}>
        <div className="expanded-project-body" style={{ overflow: "visible" }}>
          {projectJobs.map((job) => {
            const isExpanded = expandedStacks && expandedStacks[job.jobName];
            const expandedTaskForJob = getExpandedTaskForJob(job.jobId);

            if (isExpanded) {
              return (
                <div key={`expanded-${job.jobId}`} className="job-inline-window">
                  <ExpandedStackView
                    job={job}
                    searchTerm={searchTerm}
                    hasActiveFilters={hasActiveFilters}
                    openAiDialogForJob={openAiDialogForJob}
                    renderStackedImageIcon={renderStackedImageIcon}
                    selectedRow={selectedRow}
                    handleRowClick={handleRowClick}
                    handleRowDoubleClick={handleRowDoubleClick}
                    handleAddFromRow={handleAddFromRow}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    handleViewAttachments={handleViewAttachments}
                    handleReplyToNote={handleReplyToNote}
                    handlePriorityClick={handlePriorityClick}
                    handlePriorityMouseEnter={handlePriorityMouseEnter}
                    handlePriorityMouseLeave={handlePriorityMouseLeave}
                    handleNoteTextMouseEnter={handleNoteTextMouseEnter}
                    handleNoteTextMouseLeave={handleNoteTextMouseLeave}
                    handleLinkedNoteClick={handleLinkedNoteClick}
                    handleLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
                    handleLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
                    isNoteReply={isNoteReply}
                    getReplyNoteId={getReplyNoteId}
                    isOriginalNoteExists={isOriginalNoteExists}
                    userStatusMap={userStatusMap}
                    loadingUsers={loadingUsers}
                    getPriorityValue={getPriorityValue}
                    manuallyUpdatedPriorities={manuallyUpdatedPriorities}
                    shouldShowNotePopup={shouldShowNotePopup}
                    toggleStackExpansion={toggleStackExpansion}
                    userId={userId}
                    onOpenEdit={onOpenEdit}
                    onCreateTask={onCreateTask}
                    onOpenSlideshow={onOpenSlideshow}
                    tasks={tasksByJob[job.jobId] || []}
                    loadingTasks={loadingTasksByJob[job.jobId] || false}
                    expandedTasks={expandedTasks}
                    expandedTask={expandedTaskForJob}
                    fetchTasksForJob={fetchTasksForJob}
                    isAnyStackExpanded={Object.values(expandedStacks || {}).some(v => v)}
                    hasAnyTaskExpanded={Object.values(expandedTasks || {}).some(v => v)}
                    expandedStacks={expandedStacks}
                    stackedJobs={projectJobs}
                    onTaskToggle={(taskId) => {
                      let foundJobId = null;
                      for (const [jId, tasks] of Object.entries(tasksByJob)) {
                        if (tasks.some((t) => t.id === taskId)) {
                          foundJobId = parseInt(jId);
                          break;
                        }
                      }
                      if (foundJobId && toggleTaskExpansion) {
                        toggleTaskExpansion(taskId, foundJobId);
                      }
                    }}
                    onTaskClick={onTaskClick}
                    onTaskEdit={onTaskEdit}
                    onTaskDelete={onTaskDelete}
                    onTaskStatusChange={onTaskStatusChange}
                    notesByTask={notesByTask}
                    loadingNotesByTask={loadingNotesByTask}
                    onCollapseTask={() => {
                      if (expandedTaskForJob) {
                        toggleTaskExpansion(expandedTaskForJob.id, job.jobId);
                      }
                    }}
                    activeTab={activeTabByJob && activeTabByJob[job.jobId] || 0}
                    onTabChange={onTabChange}
                    TASK_STATUSES={TASK_STATUSES}
                    ALL_TASKS_TAB={ALL_TASKS_TAB}
                    highlightedTaskId={highlightedTaskId}
                    assigneeFilters={assigneeFilters}
                    jobUsers={jobUsersTask}
                    loadingUsersByJob={loadingUsersByJobTask}
                    onAssigneeFilter={onAssigneeFilter}
                    clearAssigneeFilter={clearAssigneeFilter}
                    fetchNotesForTask={fetchNotesForTask}
                    loadMoreTaskNotes={loadMoreTaskNotes}
                    taskPagination={taskPagination}
                    workspaceRole={workspaceRole}
                    fetchJobPermissionUsers={fetchJobTaskUsers}
                  />
                </div>
              );
            }

            return (
              <div key={`collapsed-${job.jobId}`} className="collapsed-job-wrapper">
                <CollapsedStackView
                  job={job}
                  searchTerm={searchTerm}
                  hasActiveFilters={hasActiveFilters}
                  openAiDialogForJob={openAiDialogForJob}
                  toggleStackExpansion={toggleStackExpansion}
                  onOpenEdit={onOpenEdit}
                  onCreateTask={onCreateTask}
                  onOpenSlideshow={onOpenSlideshow}
                  onFilterByProject={onFilterByProject}
                  onFilterByWorkspace={onFilterByWorkspace}
                  jobUsers={jobUsers}
                  loadingUsersByJob={loadingUsersByJob}
                  fetchJobPermissionUsers={fetchJobPermissionUsers}
                  workspaceRole={workspaceRole}
                  onDenyUserClick={onDenyUserClick}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

ProjectStack.propTypes = {
  project: PropTypes.object.isRequired,
  stackedJobs: PropTypes.array,
  expandedProjects: PropTypes.object.isRequired,
  toggleProjectExpansion: PropTypes.func.isRequired,
  expandedStacks: PropTypes.object,
  toggleStackExpansion: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  hasActiveFilters: PropTypes.bool,
  openAiDialogForJob: PropTypes.func.isRequired,
  renderStackedImageIcon: PropTypes.func,
  selectedRow: PropTypes.any,
  handleRowClick: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleAddFromRow: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleViewAttachments: PropTypes.func,
  handleReplyToNote: PropTypes.func,
  handlePriorityClick: PropTypes.func,
  handlePriorityMouseEnter: PropTypes.func,
  handlePriorityMouseLeave: PropTypes.func,
  handleNoteTextMouseEnter: PropTypes.func,
  handleNoteTextMouseLeave: PropTypes.func,
  handleLinkedNoteClick: PropTypes.func,
  handleLinkedNoteMouseEnter: PropTypes.func,
  handleLinkedNoteMouseLeave: PropTypes.func,
  isNoteReply: PropTypes.func,
  getReplyNoteId: PropTypes.func,
  isOriginalNoteExists: PropTypes.func,
  userStatusMap: PropTypes.object,
  loadingUsers: PropTypes.object,
  getPriorityValue: PropTypes.func,
  manuallyUpdatedPriorities: PropTypes.object,
  shouldShowNotePopup: PropTypes.func,
  userId: PropTypes.number,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  expandedTasks: PropTypes.object,
  toggleTaskExpansion: PropTypes.func,
  notesByTask: PropTypes.object,
  loadingNotesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  onTaskEdit: PropTypes.func,
  onTaskDelete: PropTypes.func,
  onTaskStatusChange: PropTypes.func,
  onTaskClick: PropTypes.func,
  refreshTasksForJob: PropTypes.func,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  onOpenEdit: PropTypes.func,
  onCreateTask: PropTypes.func,
  onOpenSlideshow: PropTypes.func,
  activeTabByJob: PropTypes.object,
  onTabChange: PropTypes.func,
  assigneeFilters: PropTypes.object,
  loadingUsersByJob: PropTypes.object,
  onAssigneeFilter: PropTypes.func,
  jobUsers: PropTypes.object,
  jobUsersTask: PropTypes.object,
  loadingUsersByJobTask: PropTypes.object,
  clearAssigneeFilter: PropTypes.func,
  fetchJobPermissionUsers: PropTypes.func,
  workspaceRole: PropTypes.string,
  onDenyUserClick: PropTypes.func,
  fetchJobTaskUsers: PropTypes.func,
};

ProjectStack.defaultProps = {
  stackedJobs: [],
  expandedStacks: {},
  tasksByJob: {},
  loadingTasksByJob: {},
  expandedTasks: {},
  notesByTask: {},
  loadingNotesByTask: {},
  assigneeFilters: {},
  loadingUsersByJob: {},
  jobUsers: {},
  jobUsersTask: {},
  loadingUsersByJobTask: {},
  activeTabByJob: {},
  taskPagination: {},
  searchTerm: '',
  hasActiveFilters: false,
  renderStackedImageIcon: () => null,
  selectedRow: null,
  handleRowClick: () => {},
  handleRowDoubleClick: () => {},
  handleAddFromRow: () => {},
  handleEdit: () => {},
  handleDelete: () => {},
  handleViewAttachments: () => {},
  handleReplyToNote: () => {},
  handlePriorityClick: () => {},
  handlePriorityMouseEnter: () => {},
  handlePriorityMouseLeave: () => {},
  handleNoteTextMouseEnter: () => {},
  handleNoteTextMouseLeave: () => {},
  handleLinkedNoteClick: () => {},
  handleLinkedNoteMouseEnter: () => {},
  handleLinkedNoteMouseLeave: () => {},
  isNoteReply: () => false,
  getReplyNoteId: () => null,
  isOriginalNoteExists: () => false,
  userStatusMap: {},
  loadingUsers: {},
  getPriorityValue: () => null,
  manuallyUpdatedPriorities: {},
  shouldShowNotePopup: () => false,
  userId: null,
  fetchTasksForJob: () => {},
  fetchNotesForTask: () => {},
  onTaskEdit: () => {},
  onTaskDelete: () => {},
  onTaskStatusChange: () => {},
  onTaskClick: () => {},
  refreshTasksForJob: () => {},
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  highlightedTaskId: null,
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  onOpenEdit: () => {},
  onCreateTask: () => {},
  onOpenSlideshow: () => {},
  onTabChange: () => {},
  onAssigneeFilter: () => {},
  clearAssigneeFilter: () => {},
  fetchJobPermissionUsers: () => {},
  workspaceRole: null,
  onDenyUserClick: () => {},
  fetchJobTaskUsers: () => {},
};

export default ProjectStack;